import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getOpportunities } from "../../apis/opportunities";
import { normalizeOpportunitiesResponse } from "../../utils/normalizeOpportunity";
import {
    MAX_OPPORTUNITIES,
    isOpportunitySelected,
    removeOpportunity,
    toggleOpportunitySelection,
} from "../../utils/opportunitySelection";
import { startCampaignFromOpportunity } from "../../store/campaignBuilderSlice";
import { APP_ROUTES, buildCampaignWizardPath } from "../../utils/campaignWizardNavigation";
import { filterPublicOpportunities } from "../../utils/pageMeta";
import { OpportunityCardCompact } from "../shared/OpportunityCard";
import OpportunitySelectionFooter from "../opportunity/OpportunitySelectionFooter";
import OpportunityPreviewSkeletons from "./skeletons/OpportunityPreviewSkeletons";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
    trackCtaClicked,
    trackFeatureStarted,
    trackItemSelected,
} from "../../analytics/analytics";
import { useScrolled } from "../../analytics/analytics.hooks";
import {
    ANALYTICS_FEATURES,
    ANALYTICS_ITEM_TYPES,
    ANALYTICS_SECTIONS,
    ANALYTICS_CTAS,
    ANALYTICS_ENTRY_POINTS,
} from "../../analytics/analytics.constants";

const PREVIEW_LIMIT = 10;

export default function OpportunityPreview({
    onOpportunitiesLoaded,
    wizardRef = APP_ROUTES.HOME,
    isDashboard = false,
    niche = null,
    sort = null,
    expandWhenSelected = true,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = useSelector((state) => state.auth.user?.uid);
    const [opportunities, setOpportunities] = useState([]);
    const [selected, setSelected] = useState([]);
    const [expandedOpportunityId, setExpandedOpportunityId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const opportunitiesSectionRef = useRef(null);

    const isMaxReached = selected.length >= MAX_OPPORTUNITIES;
    const hasSelection = selected.length > 0;

    useScrolled(
        opportunitiesSectionRef,
        ANALYTICS_SECTIONS.OPPORTUNITIES,
        !loading && !error && opportunities.length > 0
    );

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setError(null);
        setSelected([]);
        setExpandedOpportunityId(null);

        getOpportunities({
            limit: PREVIEW_LIMIT,
            ...(niche ? { niche, sort: sort ?? "relevance" } : sort ? { sort } : {}),
        })
            .then((data) => {
                if (cancelled) return;
                const normalized = normalizeOpportunitiesResponse(data);
                const items = normalized.items.slice(0, PREVIEW_LIMIT);
                setOpportunities(items);
                onOpportunitiesLoaded?.(filterPublicOpportunities(items));
            })
            .catch((err) => {
                if (cancelled) return;
                setOpportunities([]);
                onOpportunitiesLoaded?.([]);
                setError(
                    err?.response?.data?.message ||
                        err?.message ||
                        "Failed to load trends."
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [userId, onOpportunitiesLoaded, niche, sort]);

    const handleToggle = (opportunity, position) => {
        const wasSelected = isOpportunitySelected(selected, opportunity.id);
        setSelected((prev) => toggleOpportunitySelection(prev, opportunity));

        if (!wasSelected) {
            trackItemSelected(
                ANALYTICS_ITEM_TYPES.OPPORTUNITY,
                opportunity.id,
                position,
                { item_title: opportunity.title || opportunity.opportunity || "" }
            );
        }
    };

    const handleRemoveSelected = (id) => {
        setSelected((prev) => removeOpportunity(prev, id));
    };

    const handleStartCampaign = () => {
        if (selected.length === 0) return;

        trackCtaClicked(ANALYTICS_CTAS.START_CAMPAIGN);
        if (isDashboard) {
            trackFeatureStarted(ANALYTICS_FEATURES.CAMPAIGN_BUILDER, {
                entry_point: ANALYTICS_ENTRY_POINTS.DASHBOARD_OPPORTUNITY,
            });
        }

        dispatch(startCampaignFromOpportunity({ opportunities: selected, userId }));
        navigate(buildCampaignWizardPath(wizardRef, null, { fastForward: true }));
    };

    const handleViewAllClick = () => {
        trackCtaClicked(ANALYTICS_CTAS.VIEW_ALL_OPPORTUNITIES);
        if (isDashboard) {
            trackFeatureStarted(ANALYTICS_FEATURES.CONTENT_AI, {
                entry_point: ANALYTICS_ENTRY_POINTS.DASHBOARD_OPPORTUNITY,
            });
        }
    };

    return (
        <section
            ref={opportunitiesSectionRef}
            className={`-mx-2 my-10 md:mx-0 md:my-12 ${hasSelection ? "pb-28 md:pb-24" : ""}`}
        >
            <h2 className="mb-2 text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
                Choose up to {MAX_OPPORTUNITIES} trending ideas for your post
            </h2>

            {!loading && !error && opportunities.length > 0 ? (
                <p className="mb-4 text-sm text-slate-500">
                    <i className="bi bi-info-circle me-1" aria-hidden />
                    Click on trends to use them.
                </p>
            ) : null}

            {error ? (
                <p className="text-sm text-red-600">{error}</p>
            ) : loading ? (
                <OpportunityPreviewSkeletons />
            ) : opportunities.length === 0 ? (
                <p className="text-sm text-slate-500">No trends right now — check back soon.</p>
            ) : (
                <div
                    className={`grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 ${
                        expandedOpportunityId != null ? "items-start" : "items-stretch"
                    }`}
                >
                    {opportunities.map((item, index) => {
                        const id = item.id ?? index;
                        const isSelected = isOpportunitySelected(selected, item.id);
                        const isExpanded = expandedOpportunityId === id;

                        return (
                            <div
                                key={id}
                                className={`col-span-1 w-full ${isExpanded ? "self-start" : ""}`}
                            >
                                <OpportunityCardCompact
                                    className="w-full"
                                    opportunity={item}
                                    selectable
                                    isSelected={isSelected}
                                    disabled={isMaxReached && !isSelected}
                                    onToggle={() => handleToggle(item, index + 1)}
                                    showActions={expandWhenSelected}
                                    isExpanded={isExpanded}
                                    onExpandedChange={(open) =>
                                        setExpandedOpportunityId(open ? id : null)
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && !error ? (
                <div className="mt-6 flex items-center justify-between gap-3">
                    <Link
                        to={APP_ROUTES.CONTENT_AI}
                        onClick={handleViewAllClick}
                        className="btn-outline-app inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"
                    >
                        View all
                        <i className="bi bi-arrow-right" aria-hidden />
                    </Link>
                    {hasSelection ? (
                        <p className="mb-0 shrink-0 text-xs text-slate-500 sm:text-sm">
                            {selected.length}/{MAX_OPPORTUNITIES} selected
                        </p>
                    ) : null}
                </div>
            ) : null}

            <OpportunitySelectionFooter
                selected={selected}
                onRemove={handleRemoveSelected}
                onStartCampaign={handleStartCampaign}
            />
        </section>
    );
}
